import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { GraphQLClient } from "graphql-request";

import Header from "../../components/Header";
import ThemedDataGrid from "../../components/ThemedDataGrid";
import TableActions from "../../components/TableActions";
import ActionButton from "../../components/ActionButton";
import ArchiveTenantRoleModal from "../../components/DeleteTenantsRolesModal";
import { useSnackbar } from "../../components/snackbarProvider.jsx";
import StatusChip from "../../components/StatusChip";
import {
  coverageStatusTone,
  resourcingTypeTone,
} from "../../status/StatusChipTones";

import { graphqlEndpoint } from "../../config";
import { useTenant } from "../../context/TenantContext";
import { READ_TENANT_ROLES_BY_TENANT } from "../../graph/tenantRoles/queries";
import { DELETE_TENANT_ROLE } from "../../graph/tenantRoles/mutations";

const client = new GraphQLClient(graphqlEndpoint);

const ManageTenantRoles = () => {
  const navigate = useNavigate();
  const { tenantId } = useTenant();
  const { setSnackbar } = useSnackbar();

  const [rows, setRows] = useState([]);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);

  const fetchData = useCallback(async () => {
    if (!tenantId) {
      setRows([]);
      return;
    }

    try {
      const data = await client.request(READ_TENANT_ROLES_BY_TENANT, {
        tenantId,
      });

      setRows(
        (data.readTenantRolesByTenant || []).map((role) => ({
          ...role,
          id: role.id,
          departmentName: role.department?.name || "",
          reportsToRoleName: role.reportsToRole?.name || "",
        }))
      );
    } catch (err) {
      console.error("Failed to fetch tenant roles:", err);
      setRows([]);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to load roles",
      });
    }
  }, [tenantId, setSnackbar]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpenArchiveModal = (role) => {
    setSelectedRole(role);
    setArchiveModalOpen(true);
  };

  const handleCloseArchiveModal = () => {
    setArchiveModalOpen(false);
    setSelectedRole(null);
  };

  const handleConfirmArchive = async () => {
    if (!selectedRole?.id) return;

    try {
      await client.request(DELETE_TENANT_ROLE, { id: selectedRole.id });

      await fetchData();
      handleCloseArchiveModal();

      setSnackbar({
        open: true,
        severity: "success",
        message: "Role deleted successfully",
      });
    } catch (err) {
      console.error("Failed to delete tenant role:", err);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to delete role",
      });
    }
  };

const columns = [
  {
    field: "name",
    headerName: "Name",
    flex: 1.2,
  },
  {
    field: "departmentName",
    headerName: "Department",
    flex: 1,
  },
  {
    field: "resourcingType",
    headerName: "Resourcing",
    flex: 0.8,
    renderCell: ({ value }) => {
      const labels = {
        INTERNAL: "Internal",
        EXTERNAL: "External",
      };

      return (
        <StatusChip
          label={labels[value] || "Not Set"}
          tone={resourcingTypeTone(value)}
        />
      );
    },
  },
  {
    field: "coverageStatus",
    headerName: "Coverage",
    flex: 1,
    renderCell: ({ value }) => {
      const labels = {
        VACANT: "Vacant",
        COVERED: "Covered",
        TEMPORARILY_COVERED: "Temporary Cover",
      };

      return (
        <StatusChip
          label={labels[value] || "Not Set"}
          tone={coverageStatusTone(value)}
        />
      );
    },
  },
  {
    field: "reportsToRoleName",
    headerName: "Reports To",
    flex: 1,
  },
  {
    field: "actions",
    headerName: "Actions",
    flex: 0.7,
    sortable: false,
    filterable: false,
    renderCell: ({ row }) => (
      <TableActions
        onEdit={() => navigate(`/tenantRoles/edit/${row.id}`)}
        onDelete={() => handleOpenArchiveModal(row)}
        hideCreate
      />
    ),
  },
];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Header title="ROLES" subtitle="Roles own business outcomes" />
        <ActionButton
          onClick={() => navigate("/createTenantRole")}
          tone="gold"
          disabled={!tenantId}
        >
          Create Role
        </ActionButton>
      </Box>

      {!tenantId ? (
        <Box mt={4}>
          <Typography variant="h6">
            Select a tenant in the top bar to view roles.
          </Typography>
        </Box>
      ) : (
        <ThemedDataGrid rows={rows} columns={columns} />
      )}

      <ArchiveTenantRoleModal
        open={archiveModalOpen}
        onClose={handleCloseArchiveModal}
        onConfirm={handleConfirmArchive}
        role={selectedRole}
      />
    </Box>
  );
};

export default ManageTenantRoles;